import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { FaPlus, FaSearch, FaSpinner, FaTrash, FaCalculator } from 'react-icons/fa';


const Api10Form = ({ state, updateState }) => {
    const formData = state.formData || {};
    const entries = state.entries || [];
    const inputMethod = state.inputMethod || 'materials';
    const CLIMATIQ_API_KEY = process.env.REACT_APP_CLIMATIQ_API_KEY || 'Y2ZBNWZKRzRGNzJYNjRQMzpa';

    const materialTypes = {
        'steel': {
            label: 'Structural Steel (t)',
            searchQuery: 'steel production',
            preferred_unit_type: 'Mass',
            unit: 't',
            directActivityId: 'metal_products-type_structural_steel_sections'
        },
        'rebar': {
            label: 'Rebar/Plate Steel (t)',
            searchQuery: 'steel rebar production',
            preferred_unit_type: 'Mass',
            unit: 't',
            directActivityId: 'metals-type_steel_rebar'
        },
        'concrete': {
            label: 'Ready-mix Concrete (t)',
            searchQuery: 'concrete production',
            preferred_unit_type: 'Mass',
            unit: 't',
            directActivityId: 'building_materials-type_concrete'
        },
        'cement': {
            label: 'Cement (t)',
            searchQuery: 'cement production',
            preferred_unit_type: 'Mass',
            unit: 't',
            directActivityId: 'building_materials-type_cement_production'
        },
        'aluminum': {
            label: 'Aluminum (t)',
            searchQuery: 'aluminum production',
            preferred_unit_type: 'Mass',
            unit: 't',
            directActivityId: 'metals-type_aluminum_production'
        },
        'copper': {
            label: 'Copper Cable (t)',
            searchQuery: 'copper production',
            preferred_unit_type: 'Mass',
            unit: 't',
            directActivityId: 'waste_type_copper_wire-disposal_method_combusted'
        },
        'glass': {
            label: 'Glass (t)',
            searchQuery: 'glass production',
            preferred_unit_type: 'Mass',
            unit: 't',
            directActivityId: 'waste-type_glass-disposal_method_combusted'
        },
        'asphalt': {
            label: 'Asphalt (kWh)',
            searchQuery: 'asphalt production',
            preferred_unit_type: 'Energy',
            unit: 'kWh',
            directActivityId: 'fuel-type_asphalt_and_road_oil-fuel_use_stationary'
        }
    };

    const capitalCategories = ['Heavy equipment', 'Processing machinery', 'Building/Construction', 'Electrical systems', 'Vehicles', 'Rail', 'Renewable plant', 'Other'];
    const countryCodes = ['CL', 'US', 'CN', 'DE', 'JP', 'KR', 'BR', 'CA', 'AU', 'MX', 'PE', 'AR', 'CO', 'Other'];
    const currencies = ['CLP', 'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CNY', 'JPY', 'MXN', 'Other'];
    const conversionFactors = { 'concrete': 2400 };

    const searchEmissionFactors = async (materialType, country = 'CL') => {
        try {
            const config = materialTypes[materialType];
            if (!config) return null;

            // Use direct activity IDs for materials we know work
            if (config.directActivityId) {
                const directFactor = {
                    activity_id: config.directActivityId,
                    data_version: "^0"
                };

                // Special handling for materials with additional fields
                if (materialType === 'rebar') {
                    directFactor.source = "Circular Ecology";
                    directFactor.region = "GLOBAL";
                    directFactor.year = 2019;
                    directFactor.source_lca_activity = "cradle_to_gate";
                    directFactor.allowed_data_quality_flags = ["notable_methodological_variance"];
                } else if (materialType === 'steel') {
                    directFactor.source = "OEKOBAUDAT";
                    directFactor.region = "EU";
                    directFactor.year = 2021;
                    directFactor.source_lca_activity = "cradle_to_gate";
                } else if (materialType === 'aluminum') {
                    directFactor.source = "Climate TRACE";
                    directFactor.region = "TR";
                    directFactor.year = 2022;
                    directFactor.source_lca_activity = "cradle_to_gate";
                    directFactor.allowed_data_quality_flags = ["partial_factor"];
                } else if (materialType === 'copper') {
                    directFactor.source = "EPA";
                    directFactor.region = "US";
                    directFactor.year = 2022;
                    directFactor.source_lca_activity = "end_of_life";
                }
                // Glass and others use simple format with just activity_id and data_version

                const factorKey = `${materialType}_${country}`;
                updateState({ availableFactors: { ...state.availableFactors, [factorKey]: directFactor } });
                return directFactor;
            }

            // For materials without direct IDs, use search
            const params = new URLSearchParams({
                query: config.searchQuery,
                data_version: '^3',
                region: country,
                year: formData.year || new Date().getFullYear().toString(),
                results_per_page: '10'
            });

            const response = await fetch(`https://api.climatiq.io/data/v1/search?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) return null;
            const data = await response.json();

            if (data.results && data.results.length > 0) {
                const productionFactors = data.results.filter(factor =>
                    factor.source_lca_activity === 'production' ||
                    factor.activity_id.includes('production')
                );

                let bestFactor = productionFactors[0] || data.results[0];

                if (config.preferred_unit_type) {
                    const preferredFactor = productionFactors.find(factor =>
                        factor.unit_type === config.preferred_unit_type
                    ) || data.results.find(factor =>
                        factor.unit_type === config.preferred_unit_type
                    );
                    if (preferredFactor) bestFactor = preferredFactor;
                }

                const factorKey = `${materialType}_${country}`;
                updateState({ availableFactors: { ...state.availableFactors, [factorKey]: bestFactor } });
                return bestFactor;
            }

            return null;
        } catch (error) {
            console.error(`Error searching emission factors for ${materialType}:`, error);
            return null;
        }
    };

    const searchSpendFactors = async (category, country = 'CL') => {
        try {
            // Use direct activity IDs from API documentation
            const directActivityIds = {
                'Heavy equipment': 'machinery-type_construction_machinery',
                'Processing machinery': 'machinery-type_other_industrial_machinery',
                'Building/Construction': 'construction-type_buildings_and_building_construction_works',
                'Electrical systems': 'electrical_equipment-type_electrical_equipment',
                'Vehicles': 'passenger_vehicle-vehicle_type_heavy_duty_trucks-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_na',
                'Rail': 'freight_train-route_type_na-fuel_type_na',
                'Renewable plant': 'fuel-type_renewables-fuel_use_electricity_generation',
                'Other': 'machinery-type_other_industrial_machinery' // Using same as Processing machinery as fallback
            };

            const directActivityId = directActivityIds[category];

            if (directActivityId) {
                const directFactor = {
                    activity_id: directActivityId,
                    data_version: "^0"
                };

                // Add additional metadata for specific categories
                if (category === 'Heavy equipment') {
                    directFactor.source = "EPA";
                    directFactor.region = "US";
                    directFactor.year = 2018;
                    directFactor.source_lca_activity = "cradle_to_shelf";
                }

                const factorKey = `spend_${category}_${country}`;
                updateState({ availableFactors: { ...state.availableFactors, [factorKey]: directFactor } });
                return directFactor;
            }

            // For categories without direct IDs, use search
            const params = new URLSearchParams({
                query: `${category} capital goods`,
                data_version: '^3',
                region: country,
                year: formData.year || new Date().getFullYear().toString(),
                results_per_page: '10'
            });

            const response = await fetch(`https://api.climatiq.io/data/v1/search?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) return null;
            const data = await response.json();

            if (data.results && data.results.length > 0) {
                const spendFactors = data.results.filter(factor => factor.unit_type === 'Money');
                const bestFactor = spendFactors[0] || data.results[0];

                const factorKey = `spend_${category}_${country}`;
                updateState({ availableFactors: { ...state.availableFactors, [factorKey]: bestFactor } });
                return bestFactor;
            }

            return null;
        } catch (error) {
            console.error(`Error searching spend factors for ${category}:`, error);
            return null;
        }
    };

    const handleSearchFactors = async () => {
        if (entries.length === 0) {
            toastr.error('Please add at least one capital goods entry first');
            return;
        }

        updateState({ isSearching: true, availableFactors: {} });
        toastr.info('Searching for emission factors...');

        try {
            if (inputMethod === 'materials') {
                const materialPromises = [];
                entries.forEach(entry => {
                    Object.keys(materialTypes).forEach(materialType => {
                        if (entry.materials?.[materialType]) {
                            materialPromises.push(searchEmissionFactors(materialType, entry.supplierCountry));
                        }
                    });
                });
                await Promise.allSettled(materialPromises);
            } else {
                const spendPromises = entries.map(entry =>
                    searchSpendFactors(entry.category, entry.supplierCountry)
                );
                await Promise.allSettled(spendPromises);
            }

            toastr.success('Factor search completed! Click Calculate to proceed.');
        } catch (error) {
            toastr.error('Search failed. Please check your API key and try again.');
        } finally {
            updateState({ isSearching: false });
        }
    };

    const calculateMaterialsEmissions = async (entry, index) => {
        try {
            let totalCo2e = 0;
            const materialResults = {};

            for (const [materialType, quantity] of Object.entries(entry.materials || {})) {
                if (!quantity || parseFloat(quantity) <= 0) continue;

                const factorKey = `${materialType}_${entry.supplierCountry}`;
                const emissionFactor = state.availableFactors?.[factorKey];

                if (!emissionFactor) {
                    toastr.warning(`No emission factor found for ${materialTypes[materialType]?.label}`);
                    continue;
                }

                let apiQuantity = parseFloat(quantity);
                let apiUnit = materialTypes[materialType]?.unit;

                let requestBody;

                // Use exact payload formats from documentation for each material type
                if (emissionFactor.data_version === "^0") {
                    if (materialType === 'steel') {
                        // Exact format for Structural Steel from documentation
                        requestBody = {
                            emission_factor: {
                                activity_id: "metal_products-type_structural_steel_sections",
                                source: "OEKOBAUDAT",
                                region: "EU",
                                year: 2021,
                                source_lca_activity: "cradle_to_gate",
                                data_version: "^0"
                            },
                            parameters: {
                                weight: apiQuantity,
                                weight_unit: apiUnit
                            }
                        };
                    } else if (materialType === 'aluminum') {
                        // Exact format for Aluminum from documentation
                        requestBody = {
                            emission_factor: {
                                activity_id: "metals-type_aluminum_production",
                                source: "Climate TRACE",
                                region: "TR",
                                year: 2022,
                                source_lca_activity: "cradle_to_gate",
                                data_version: "^0",
                                allowed_data_quality_flags: [
                                    "partial_factor"
                                ]
                            },
                            parameters: {
                                weight: apiQuantity,
                                weight_unit: apiUnit
                            }
                        };
                    } else if (materialType === 'copper') {
                        // Exact format for Copper Cable from documentation
                        requestBody = {
                            emission_factor: {
                                activity_id: "waste_type_copper_wire-disposal_method_combusted",
                                source: "EPA",
                                region: "US",
                                year: 2022,
                                source_lca_activity: "end_of_life",
                                data_version: "^0"
                            },
                            parameters: {
                                weight: apiQuantity,
                                weight_unit: apiUnit
                            }
                        };
                    } else if (materialType === 'glass') {
                        // Exact format for Glass from documentation
                        requestBody = {
                            emission_factor: {
                                activity_id: "waste-type_glass-disposal_method_combusted",
                                data_version: "^0"
                            },
                            parameters: {
                                weight: apiQuantity,
                                weight_unit: apiUnit
                            }
                        };
                    } else if (materialType === 'asphalt') {
                        // Asphalt uses energy parameters as per documentation
                        requestBody = {
                            emission_factor: {
                                activity_id: emissionFactor.activity_id,
                                data_version: "^0"
                            },
                            parameters: {
                                energy: apiQuantity,
                                energy_unit: apiUnit
                            }
                        };
                    } else if (materialType === 'rebar') {
                        // Rebar uses the format that was working
                        requestBody = {
                            emission_factor: {
                                activity_id: emissionFactor.activity_id,
                                source: emissionFactor.source,
                                region: emissionFactor.region,
                                year: emissionFactor.year,
                                source_lca_activity: emissionFactor.source_lca_activity,
                                data_version: "^0",
                                allowed_data_quality_flags: emissionFactor.allowed_data_quality_flags || []
                            },
                            parameters: {
                                weight: apiQuantity,
                                weight_unit: apiUnit
                            }
                        };
                    } else {
                        // Concrete, cement and others use simple format with weight
                        requestBody = {
                            emission_factor: {
                                activity_id: emissionFactor.activity_id,
                                data_version: "^0"
                            },
                            parameters: {
                                weight: apiQuantity,
                                weight_unit: apiUnit
                            }
                        };
                    }
                } else {
                    // Search-based factors use full format (keep existing logic)
                    if (emissionFactor.unit_type === 'Mass') {
                        requestBody = {
                            emission_factor: {
                                activity_id: emissionFactor.activity_id,
                                source: emissionFactor.source,
                                region: emissionFactor.region,
                                year: parseInt(emissionFactor.year),
                                data_version: "^3"
                            },
                            parameters: {
                                mass: apiQuantity,
                                mass_unit: apiUnit
                            }
                        };
                    } else if (emissionFactor.unit_type === 'Volume') {
                        requestBody = {
                            emission_factor: {
                                activity_id: emissionFactor.activity_id,
                                source: emissionFactor.source,
                                region: emissionFactor.region,
                                year: parseInt(emissionFactor.year),
                                data_version: "^3"
                            },
                            parameters: {
                                volume: apiQuantity,
                                volume_unit: apiUnit
                            }
                        };
                    } else if (emissionFactor.unit_type === 'Energy') {
                        requestBody = {
                            emission_factor: {
                                activity_id: emissionFactor.activity_id,
                                source: emissionFactor.source,
                                region: emissionFactor.region,
                                year: parseInt(emissionFactor.year),
                                data_version: "^3"
                            },
                            parameters: {
                                energy: apiQuantity,
                                energy_unit: apiUnit
                            }
                        };
                    } else {
                        requestBody = {
                            emission_factor: {
                                activity_id: emissionFactor.activity_id,
                                source: emissionFactor.source,
                                region: emissionFactor.region,
                                year: parseInt(emissionFactor.year),
                                data_version: "^3"
                            },
                            parameters: {
                                weight: apiQuantity,
                                weight_unit: apiUnit
                            }
                        };
                    }
                }

                const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody)
                });

                if (!response.ok) {
                    const errorData = await response.text();
                    throw new Error(`API request failed with status ${response.status}: ${errorData}`);
                }

                const data = await response.json();
                totalCo2e += data.co2e;
                materialResults[materialType] = {
                    quantity: parseFloat(quantity),
                    unit: materialTypes[materialType]?.unit,
                    co2e: data.co2e
                };
            }

            return { totalCo2e, materialResults };
        } catch (error) {
            console.error(`Error calculating materials emissions:`, error);
            throw error;
        }
    };

    const calculateSpendEmissions = async (entry, index) => {
        try {
            const factorKey = `spend_${entry.category}_${entry.supplierCountry}`;
            const emissionFactor = state.availableFactors?.[factorKey];

            if (!emissionFactor) {
                throw new Error(`No emission factor found for ${entry.category}`);
            }

            const spend = parseFloat(entry.annualSpend);

            // Ensure currency has a default value to prevent undefined error
            const currency = entry.currency || 'CLP';

            let requestBody;

            // Use exact payload formats from API documentation for each category
            if (entry.category === 'Heavy equipment') {
                // Heavy equipment with full metadata from documentation
                requestBody = {
                    emission_factor: {
                        activity_id: "machinery-type_construction_machinery",
                        source: "EPA",
                        region: "US",
                        year: 2018,
                        source_lca_activity: "cradle_to_shelf",
                        data_version: "^0"
                    },
                    parameters: {
                        money: spend,
                        money_unit: currency.toLowerCase()
                    }
                };
            } else if (entry.category === 'Rail') {
                // Rail with simple format from documentation
                requestBody = {
                    emission_factor: {
                        activity_id: "freight_train-route_type_na-fuel_type_na",
                        data_version: "^0"
                    },
                    parameters: {
                        money: spend,
                        money_unit: currency.toLowerCase()
                    }
                };
            } else if (entry.category === 'Renewable plant') {
                // Renewable plant uses energy parameters according to documentation
                requestBody = {
                    emission_factor: {
                        activity_id: "fuel-type_renewables-fuel_use_electricity_generation",
                        data_version: "^0"
                    },
                    parameters: {
                        energy: spend, // Using spend as energy for now, but this might need adjustment
                        energy_unit: "kWh"
                    }
                };
            } else {
                // All other categories use money parameters with direct activity IDs
                requestBody = {
                    emission_factor: {
                        activity_id: emissionFactor.activity_id,
                        data_version: "^0"
                    },
                    parameters: {
                        money: spend,
                        money_unit: currency.toLowerCase()
                    }
                };
            }

            const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`API request failed with status ${response.status}: ${errorData}`);
            }

            const data = await response.json();
            return data.co2e;
        } catch (error) {
            console.error(`Error calculating spend emissions:`, error);
            throw error;
        }
    };

    const handleCalculate = async () => {
        if (entries.length === 0) {
            toastr.error('Please add at least one capital goods entry');
            return;
        }

        if (Object.keys(state.availableFactors || {}).length === 0) {
            toastr.error('Please search for emission factors first');
            return;
        }

        updateState({ isCalculating: true, calculationResults: {} });
        toastr.info('Calculating emissions...');

        try {
            const results = {};
            let hasSuccessfulCalculation = false;

            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];

                try {
                    let co2e = 0;
                    let calculationData = {};

                    if (inputMethod === 'materials') {
                        const materialsResult = await calculateMaterialsEmissions(entry, i);
                        co2e = materialsResult.totalCo2e;
                        calculationData = { materials: materialsResult.materialResults };
                    } else {
                        co2e = await calculateSpendEmissions(entry, i);
                        calculationData = { spend: parseFloat(entry.annualSpend), currency: entry.currency || 'CLP' };
                    }

                    const resultKey = `capital_${i}`;
                    results[resultKey] = {
                        description: entry.description,
                        category: entry.category,
                        co2e,
                        co2e_unit: 'kg',
                        calculation_method: 'climatiq_api',
                        calculation_data: calculationData,
                        supplierCountry: entry.supplierCountry,
                        period: entry.period
                    };

                    toastr.success(`Calculated emissions for ${entry.description || entry.category}: ${co2e.toFixed(2)} kg CO₂e`);
                    hasSuccessfulCalculation = true;
                } catch (error) {
                    console.error(`Calculation error for capital goods entry ${i + 1}:`, error);
                    toastr.error(`Failed to calculate emissions for entry ${i + 1}: ${error.message}`);
                }
            }

            updateState({ calculationResults: results });

            if (hasSuccessfulCalculation) {
                const totalEmissions = Object.values(results).reduce((sum, result) => sum + (result.co2e || 0), 0);
                toastr.success(`Calculation complete! Total emissions: ${totalEmissions.toFixed(2)} kg CO₂e`);
            }
        } catch (error) {
            toastr.error('Calculation failed. Please check your inputs and try again.');
            console.error('Calculation error:', error);
        } finally {
            updateState({ isCalculating: false });
        }
    };

    const handleAddEntry = () => {
        const newEntries = [...entries, {
            description: '',
            category: '',
            materials: {},
            annualSpend: '',
            currency: 'CLP',
            supplierCountry: 'CL',
            period: `${formData.year || new Date().getFullYear()} (Jan–Dec)`,
            evidence: null
        }];
        updateState({ entries: newEntries });
    };

    const handleEntryChange = (index, field, value) => {
        const updated = [...entries];
        updated[index][field] = value;
        updateState({ entries: updated });
    };

    const handleMaterialChange = (index, materialType, value) => {
        const updated = [...entries];
        if (!updated[index].materials) updated[index].materials = {};
        updated[index].materials[materialType] = value;
        updateState({ entries: updated });
    };

    const handleRemoveEntry = (index) => {
        updateState({ entries: entries.filter((_, i) => i !== index) });
    };

    const loadSampleData = () => {
        updateState({
            formData: { ...formData, year: '2025' },
            entries: [{
                description: 'New processing facility',
                category: 'Processing machinery',
                materials: { steel: '500', concrete: '1200' },
                supplierCountry: 'CL',
                period: '2025 (Jan–Dec)',
                evidence: null
            }]
        });
        toastr.info('Sample data loaded. Click "Search Factors" to find emission factors.');
    };

    const totalEmissions = Object.values(state.calculationResults || {}).reduce((sum, result) => sum + (result.co2e || 0), 0);

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 pb-24">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-8">
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow_200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            <strong>API Status:</strong> {CLIMATIQ_API_KEY && CLIMATIQ_API_KEY !== 'Y2ZBNWZKRzRGNzJYNjRQMzpa' ?
                                '✅ API Key Configured' : '❌ Please set REACT_APP_CLIMATIQ_API_KEY in .env file'}
                        </p>
                    </div>

                    <div className="flex justify-end mb-6">
                        <button
                            onClick={loadSampleData}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 text-sm"
                        >
                            Load Sample Data
                        </button>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Year *</label>
                        <select
                            value={formData.year || new Date().getFullYear().toString()}
                            onChange={(e) => updateState({ formData: { ...formData, year: e.target.value } })}
                            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            {Array.from({ length: new Date().getFullYear() - 2009 }, (_, i) =>
                                (new Date().getFullYear() - i).toString()
                            ).map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">10.2 Data Input Method</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <button
                                onClick={() => updateState({ inputMethod: 'materials' })}
                                className={`p-6 rounded-lg border-2 transition-all text-left ${inputMethod === 'materials'
                                        ? 'border-green-500 bg-green-50 text-green-700'
                                        : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                <div className="font-semibold text-lg">Materials-based (Preferred)</div>
                                <div className="text-sm mt-2">Provide material quantities (steel, concrete, etc.).</div>
                            </button>
                            <button
                                onClick={() => updateState({ inputMethod: 'spend' })}
                                className={`p-6 rounded-lg border-2 transition-all text-left ${inputMethod === 'spend'
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                <div className="font-semibold text-lg">Spend-based (Fallback)</div>
                                <div className="text-sm mt-2">Provide annual spend by category.</div>
                            </button>
                        </div>
                    </div>

                    <div className="mb-6">
                        <button
                            onClick={handleSearchFactors}
                            disabled={state.isSearching}
                            className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isSearching ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaSearch className="w-4 h-4" />
                            )}
                            <span>{state.isSearching ? 'Searching...' : 'Search Emission Factors'}</span>
                        </button>
                    </div>

                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">10.2 Capital Goods</h3>

                        <button
                            onClick={handleAddEntry}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 mb-6"
                        >
                            <FaPlus className="w-4 h-4" />
                            <span>Add Capital Goods Entry</span>
                        </button>

                        {entries.map((entry, index) => {
                            const resultKey = `capital_${index}`;
                            const result = state.calculationResults?.[resultKey];

                            return (
                                <div key={index} className="p-6 border-2 border-gray-200 rounded-lg space-y-4 mb-4">
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-semibold">Capital Goods Entry {index + 1}</h4>
                                        <button
                                            onClick={() => handleRemoveEntry(index)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <FaTrash className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {result && (
                                        <div className="p-3 bg-green-100 rounded-lg border border-green-200">
                                            <p className="text-sm text-green-800 font-medium">
                                                Emissions: {result.co2e.toFixed(2)} {result.co2e_unit}
                                            </p>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="e.g., New processing facility"
                                                value={entry.description}
                                                onChange={(e) => handleEntryChange(index, 'description', e.target.value)}
                                            />
                                        </div>
                                        {inputMethod === 'materials' ? (
                                            <>
                                                {Object.keys(materialTypes).map(materialType => (
                                                    <div key={materialType}>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            {materialTypes[materialType].label}
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                            placeholder="Enter quantity"
                                                            value={entry.materials?.[materialType] || ''}
                                                            onChange={(e) => handleMaterialChange(index, materialType, e.target.value)}
                                                        />
                                                    </div>
                                                ))}
                                            </>
                                        ) : (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                                                    <select
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        value={entry.category}
                                                        onChange={(e) => handleEntryChange(index, 'category', e.target.value)}
                                                    >
                                                        <option value="">Select category</option>
                                                        {capitalCategories.map(cat => (
                                                            <option key={cat} value={cat}>{cat}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Annual Spend *</label>
                                                    <div className="flex items-center space-x-2">
                                                        <input
                                                            type="number"
                                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                            placeholder="Enter amount"
                                                            value={entry.annualSpend}
                                                            onChange={(e) => handleEntryChange(index, 'annualSpend', e.target.value)}
                                                        />
                                                        <select
                                                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                                                            value={entry.currency || 'CLP'}
                                                            onChange={(e) => handleEntryChange(index, 'currency', e.target.value)}
                                                        >
                                                            {currencies.map(curr => (
                                                                <option key={curr} value={curr}>{curr}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Country *</label>
                                            <select
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                value={entry.supplierCountry}
                                                onChange={(e) => handleEntryChange(index, 'supplierCountry', e.target.value)}
                                            >
                                                {countryCodes.map(code => (
                                                    <option key={code} value={code}>{code}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Period</label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="e.g., 2024 (Jan–Dec)"
                                                value={entry.period}
                                                onChange={(e) => handleEntryChange(index, 'period', e.target.value)}
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Evidence Upload</label>
                                            <input
                                                type="file"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                onChange={(e) => handleEntryChange(index, 'evidence', e.target.files[0])}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {Object.keys(state.calculationResults || {}).length > 0 && (
                        <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Calculation Results Summary</h3>
                            <div className="space-y-3">
                                {Object.entries(state.calculationResults).map(([key, result]) => (
                                    <div key={key} className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
                                        <div>
                                            <span className="font-medium text-gray-700">
                                                {result.description || result.category}
                                            </span>
                                        </div>
                                        <span className="font-semibold text-green-700">
                                            {result.co2e.toFixed(2)} {result.co2e_unit}
                                        </span>
                                    </div>
                                ))}
                                <div className="border-t border-gray-300 pt-3 mt-2">
                                    <div className="flex justify-between items-center font-bold text-lg">
                                        <span>Total Capital Goods Emissions:</span>
                                        <span className="text-blue-700">
                                            {totalEmissions.toFixed(2)} kg CO₂e
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            onClick={handleCalculate}
                            disabled={state.isCalculating || Object.keys(state.availableFactors || {}).length === 0}
                            className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isCalculating ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaCalculator className="w-4 h-4" />
                            )}
                            <span>{state.isCalculating ? 'Calculating...' : 'Calculate'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Api10Form;